import { ReactNode, useCallback, useEffect, useState } from 'react';
import { CloseIcon, ModalContainer, ModalRoot } from './styles';

import { useTheme } from 'styled-components';
import Icon from '../Icon';

interface ModalProps {
  visible: boolean;
  onClose?: () => void;
  title?: string;
  content?: ReactNode;
  contentPadding?: string;
  maxHeight?: string;
  width?: string;
  backgroundColor?: string;
  contentBackgroundColor?: string;
  headerIcon?: JSX.Element;
}

const Modal = ({
  visible,
  onClose,
  title,
  content,
  contentPadding,
  maxHeight,
  width,
  backgroundColor,
  contentBackgroundColor,
  headerIcon,
}: ModalProps) => {
  const theme = useTheme();

  const [activateAnimation, setActivateAnimation] = useState<boolean>(visible);

  useEffect(() => {
    setActivateAnimation(visible);
  }, [visible]);

  const stopPropagation = (e: Pick<Event, 'stopPropagation'>) => {
    e.stopPropagation();
  };

  const onClick = useCallback(() => {
    setActivateAnimation(prevState => !prevState);
    setTimeout(() => {
      onClose?.();
    }, 1000);
  }, [onClose]);

  // ⌨️ Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        onClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClick]);

  return (
    <ModalRoot $mounted={visible} $shouldScroll={!maxHeight}>
      <ModalContainer
        onClick={stopPropagation}
        $width={width}
        $maxHeight={maxHeight}
        $backgroundColor={backgroundColor}
        $activateAnimation={activateAnimation}
      >
        <CloseIcon onClick={onClick}>
          <Icon color={theme.color.shadeGrey2} indentLess name="close" />
        </CloseIcon>
        <div style={{ overflowY: 'auto' }}>{content}</div>
      </ModalContainer>
    </ModalRoot>
  );
};

export default Modal;
